import {
  faBan,
  faCheckCircle,
  faCheckDouble,
  faChevronDown,
  faPrint,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import Flex from 'components/common/Flex';
import DialogModal from 'components/modals/DialogModal';
import { formatDateTime } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import InvoiceService from 'services/Invoice.service';

const InvoiceViewModal = ({ show, onHide, invoiceId, onStatusUpdated }) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [returnItemsToPending, setReturnItemsToPending] = useState(true);

  useEffect(() => {
    if (show && invoiceId) {
      fetchInvoice();
    } else {
      setInvoice(null);
    }
  }, [show, invoiceId]);

  const fetchInvoice = async () => {
    setIsLoading(true);
    try {
      const response = await InvoiceService.findById(invoiceId);
      if (response && response.id) {
        setInvoice(response);
      } else {
        showErrorNotification('Fatura não encontrada');
        onHide();
      }
    } catch (error) {
      console.error('Erro ao buscar fatura:', error);
      showErrorNotification('Erro ao carregar fatura');
      onHide();
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getInvoiceStatusBadge = status => {
    const variants = {
      PENDING: 'warning',
      APPROVED: 'success',
      COMPLETED: 'info',
      CANCELED: 'danger',
      DRAFT: 'warning'
    };
    const labels = {
      PENDING: 'Pendente',
      APPROVED: 'Aprovada',
      COMPLETED: 'Concluída',
      CANCELED: 'Cancelada',
      DRAFT: 'Rascunho'
    };
    return (
      <Badge bg={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const handleExportPdf = async () => {
    if (!invoiceId) return;
    setIsExporting(true);
    try {
      const response = await InvoiceService.exportPdf(invoiceId);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fatura-${invoice?.code || invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSuccessNotification('PDF exportado com sucesso');
      } else {
        showErrorNotification('Erro ao exportar PDF');
      }
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      showErrorNotification('Erro ao exportar PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpdateStatus = async (newStatus, returnItems = false) => {
    if (!invoiceId) return;
    setIsUpdatingStatus(true);
    try {
      const response = await InvoiceService.updateStatus(
        invoiceId,
        newStatus,
        newStatus === 'CANCELED' ? returnItems : undefined
      );
      if (response && (response.id || response.status === 200)) {
        const statusLabels = {
          APPROVED: 'Aprovada',
          COMPLETED: 'Concluída',
          CANCELED: 'Cancelada'
        };
        showSuccessNotification(
          `Fatura ${statusLabels[newStatus] || 'atualizada'} com sucesso!`
        );
        // Recarregar a fatura para atualizar o status
        await fetchInvoice();
        // Chamar callback para atualizar a listagem
        if (onStatusUpdated) {
          onStatusUpdated();
        }
      } else {
        showErrorNotification(
          response.userMessage || 'Erro ao atualizar status da fatura'
        );
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showErrorNotification('Erro ao atualizar status da fatura');
    } finally {
      setIsUpdatingStatus(false);
      setShowCancelDialog(false);
    }
  };

  const handleCancelClick = () => {
    setReturnItemsToPending(true);
    setShowCancelDialog(true);
  };

  const getStatusActions = () => {
    if (!invoice || !invoice.status) return null;

    const status = invoice.status;

    // Status terminais: COMPLETED e CANCELED não permitem mais alterações
    if (status === 'COMPLETED' || status === 'CANCELED') {
      return null;
    }

    // DRAFT: botão principal "Aprovar", dropdown com "Concluir" e "Cancelar"
    if (status === 'DRAFT') {
      return (
        <ButtonGroup>
          <Button
            onClick={() => handleUpdateStatus('APPROVED')}
            disabled={isUpdatingStatus}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Aprovar
          </Button>
          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle
              split
              disabled={isUpdatingStatus}
              as={CustomToggle}
            />
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleUpdateStatus('COMPLETED')}>
                <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
                Concluir
              </Dropdown.Item>
              <Dropdown.Item onClick={handleCancelClick}>
                <FontAwesomeIcon icon={faBan} className="me-2" />
                Cancelar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>
      );
    }

    // APPROVED: botão principal "Concluir", dropdown com "Cancelar"
    if (status === 'APPROVED') {
      return (
        <ButtonGroup>
          <Button
            onClick={() => handleUpdateStatus('COMPLETED')}
            disabled={isUpdatingStatus}
          >
            <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
            Concluir
          </Button>
          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle
              split
              disabled={isUpdatingStatus}
              as={CustomToggle}
            />
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleCancelClick}>
                <FontAwesomeIcon icon={faBan} className="me-2" />
                Cancelar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>
      );
    }

    // Outros status: não mostrar ações
    return null;
  };

  const calculateTotals = () => {
    if (!invoice || !invoice.billableItems) {
      return { products: 0, services: 0, total: 0 };
    }

    let productsTotal = 0;
    let servicesTotal = 0;

    invoice.billableItems.forEach(item => {
      const itemTotal = item.totalPrice || item.quantity * item.unitPrice;
      // Verifica o tipo através de diferentes possíveis estruturas de dados
      const itemType =
        item.productService?.type || item.type || item.productServiceType;

      if (itemType === 'PRODUCT') {
        productsTotal += itemTotal;
      } else {
        // Por padrão, considera como serviço se não for produto
        servicesTotal += itemTotal;
      }
    });

    return {
      products: productsTotal,
      services: servicesTotal,
      total: productsTotal + servicesTotal
    };
  };

  const totals = calculateTotals();

  const columns = useMemo(
    () => [
      {
        accessorKey: 'ticket.code',
        header: 'Ticket',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { ticket } = rowData.row.original;
          return (
            <Link to={`/tickets/${ticket.id}`} target="_blank">
              #{ticket.code} {ticket.subject}
            </Link>
          );
        }
      },
      {
        accessorKey: 'productService.name',
        header: 'Item',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { productService } = rowData.row.original;
          return <div>{productService.name || '-'}</div>;
        }
      },
      {
        accessorKey: 'quantity',
        header: 'Quantidade',
        meta: {
          headerProps: { className: 'text-900 text-center' },
          cellProps: { className: 'text-center' }
        },
        cell: rowData => {
          const { quantity } = rowData.row.original;
          return <div>{quantity}</div>;
        }
      },
      {
        accessorKey: 'unitPrice',
        header: 'Preço Unit.',
        meta: {
          headerProps: { className: 'text-900 text-end' },
          cellProps: { className: 'text-end' }
        },
        cell: rowData => {
          const { unitPrice } = rowData.row.original;
          return <div>{formatCurrency(unitPrice)}</div>;
        }
      },
      {
        accessorKey: 'total',
        header: 'Total',
        meta: {
          headerProps: { className: 'text-900 text-end' },
          cellProps: { className: 'text-end' }
        },
        cell: rowData => {
          const { totalPrice, quantity, unitPrice } = rowData.row.original;
          return (
            <div className="fw-bold">
              {formatCurrency(totalPrice || quantity * unitPrice)}
            </div>
          );
        }
      }
    ],
    []
  );

  const tableData = useMemo(() => {
    if (!invoice || !invoice.billableItems) return [];
    return invoice.billableItems.map(item => ({
      ...item,
      total: item.totalPrice || item.quantity * item.unitPrice
    }));
  }, [invoice]);

  const table = useAdvanceTable({
    data: tableData,
    columns,
    selection: false
  });

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="border-bottom">
        <Modal.Title className="fw-bold">Detalhes da Fatura</Modal.Title>
        <Button
          variant="link"
          className="p-0 text-900"
          onClick={onHide}
          style={{ fontSize: '1.5rem', lineHeight: 1 }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <Flex justifyContent="center" className="py-5">
            <Spinner animation="border" variant="primary" />
          </Flex>
        ) : invoice ? (
          <>
            <Row className="mb-4">
              <Col md={6}>
                <div className="mb-2">
                  <span>Número</span>
                  <p className="m-0 fw-bold text-muted">
                    {invoice.code || `#${invoice.id}`}
                  </p>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <span>Cliente</span>
                  <p className="m-0 fw-bold text-muted">
                    {invoice.customer.name || '-'}
                  </p>
                </div>
              </Col>
              {invoice.creationDate && (
                <Col md={6}>
                  <div className="mb-2">
                    <span>Data Emissão</span>
                    <p className="m-0 fw-bold text-muted">
                      {formatDateTime(invoice.creationDate)}
                    </p>
                  </div>
                </Col>
              )}
              <Col md={6}>
                <div className="mb-2">
                  <strong>Status</strong>
                  <p className="m-0 fw-bold text-muted">
                    {getInvoiceStatusBadge(invoice.status)}
                  </p>
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <h6 className="mb-3">Itens</h6>
              {!invoice.billableItems || invoice.billableItems.length === 0 ? (
                <p className="text-muted">Nenhum item encontrado</p>
              ) : (
                <>
                  <AdvanceTableProvider {...table}>
                    <AdvanceTable
                      headerClassName="text-nowrap align-middle border-0"
                      rowClassName="align-middle border-0"
                      tableProps={{
                        className: 'mb-0',
                        borderless: true
                      }}
                    />
                  </AdvanceTableProvider>
                </>
              )}
            </div>

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center">
                <span>Total Produtos</span>
                <span>{formatCurrency(totals.products)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span>Total Serviços</span>
                <span>{formatCurrency(totals.services)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2">
                <span className="fw-bold text-muted">Total Geral</span>
                <span className="fw-bold fs-7 text-muted">
                  {formatCurrency(totals.total)}
                </span>
              </div>
            </div>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="border-top">
        <Flex alignItems="center" className="gap-2">
          <Button variant="falcon-default" onClick={onHide}>
            Fechar
          </Button>
          <Button
            variant="primary"
            onClick={handleExportPdf}
            disabled={isExporting || !invoice}
          >
            <FontAwesomeIcon icon={faPrint} className="me-2" />
            {isExporting ? 'Exportando...' : 'Imprimir PDF'}
          </Button>
          <div>{getStatusActions()}</div>
        </Flex>
      </Modal.Footer>
      <DialogModal
        showModal={showCancelDialog}
        handleClose={() => setShowCancelDialog(false)}
        response={confirmed => {
          if (confirmed) {
            handleUpdateStatus('CANCELED', returnItemsToPending);
          } else {
            setShowCancelDialog(false);
          }
        }}
        message={
          <div className="text-start">
            <p className="mb-3">Tem certeza que deseja cancelar esta fatura?</p>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="return-items-check-modal"
                checked={returnItemsToPending}
                onChange={e => setReturnItemsToPending(e.target.checked)}
              />
              <label
                className="form-check-label"
                htmlFor="return-items-check-modal"
              >
                Retornar itens para pendente (podem ser faturados novamente)
              </label>
            </div>
          </div>
        }
        title="Cancelar Fatura"
        confirmText="Confirmar Cancelamento"
      />
    </Modal>
  );
};

export default InvoiceViewModal;

const CustomToggle = forwardRef(({ onClick, disabled }, ref) => (
  <Button
    style={{ borderRadius: '0 20px 20px 0', height: '100%' }}
    ref={ref}
    disabled={disabled}
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faChevronDown} />
  </Button>
));
